import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { Calendar, FileText, Handshake, Phone, Target, Headphones } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import { FilterDismissLink } from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import ContactOutcomeModal, { OutcomeSummaryBadge } from "../../components/dashboard/ContactOutcomeModal";
import {
  CONTACT_CALLS_FILTER_CONFIG,
  PLANS_FILTER_CONFIG,
  PTP_FILTER_CONFIG,
} from "../../config/tableFilterConfigs";
import {
  MOCK_CONTACT_LOGS,
  MOCK_PAYMENT_PLANS,
  MOCK_PROMISES_TO_PAY,
} from "../../data/mock/dashboardData";
import useRole from "../../hooks/useRole";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchOpsCallLogs, fetchOpsCallLogsSummary } from "../../services/dashboardApi";
import { fetchOpsPaymentPlans } from "../../services/paymentApi";
import { showError } from "../../utils/toast";
import { isPlaceholderName, personNameColumns } from "../../utils/personName";

const TABS = [
  { id: "calls", label: "Call logs" },
  { id: "ptp", label: "Promises to pay" },
  { id: "plans", label: "Payment plans" },
];

const VALID_TABS = new Set(TABS.map((t) => t.id));

function planCustomerName(plan) {
  const joined = [plan?.first_name, plan?.last_name].filter(Boolean).join(" ").trim();
  const candidates = [joined, plan?.customer_name, plan?.customer, plan?.fullname];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text && text !== "—" && !/^customer$/i.test(text)) return text;
  }
  return null;
}

export default function ContactCentrePage() {
  const [searchParams] = useSearchParams();
  const rawCustomer = searchParams.get("customer");
  const customerParam = rawCustomer && !isPlaceholderName(rawCustomer) ? rawCustomer : null;
  const tabParam = searchParams.get("tab");
  const [tab, setTab] = useState("calls");
  const [selectedLog, setSelectedLog] = useState(null);
  const { isCallCenterAgent, isCallCenterManager, isLegalOfficer, isSuperAdmin, isExecutiveManager, user } = useRole();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [liveLogs, setLiveLogs] = useState([]);
  const [livePlans, setLivePlans] = useState([]);
  const [callSummary, setCallSummary] = useState(null);
  const [loading, setLoading] = useState(useLiveApi);

  useEffect(() => {
    if (!useLiveApi) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      fetchOpsCallLogs(token),
      fetchOpsCallLogsSummary(token),
      fetchOpsPaymentPlans(token),
    ])
      .then(([logsResult, summaryResult, plansResult]) => {
        if (cancelled) return;
        if (logsResult.status === "fulfilled") setLiveLogs(logsResult.value || []);
        else showError(logsResult.reason?.message || "Could not load call logs.");
        if (summaryResult.status === "fulfilled") setCallSummary(summaryResult.value || null);
        if (plansResult.status === "fulfilled") setLivePlans(plansResult.value || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const activeTab = customerParam
    ? VALID_TABS.has(tabParam)
      ? tabParam
      : "calls"
    : tab;

  const initialFilters = useMemo(() => {
    if (!customerParam) return undefined;
    return { customer: [customerParam] };
  }, [customerParam]);

  // Agents only see their own contact history, promises and plans here —
  // same server-side scoping requirement as the Cases page.
  const contactLogs = useMemo(() => {
    const source = useLiveApi ? liveLogs : MOCK_CONTACT_LOGS;
    if (!isCallCenterAgent || useLiveApi) return source;
    return source.filter((l) => l.agent === user?.name);
  }, [useLiveApi, liveLogs, isCallCenterAgent, user?.name]);
  const promisesToPay = useMemo(() => {
    if (useLiveApi) {
      return contactLogs
        .filter((log) => Number(log.promisedAmount) > 0)
        .map((log) => ({
          id: `PTP-${log.id}`,
          customer: log.customer,
          caseId: log.caseId,
          amount: log.promisedAmount,
          dueDate: log.dueDate,
          status: "pending",
          recordedBy: log.recordedBy,
        }));
    }
    return isCallCenterAgent
      ? MOCK_PROMISES_TO_PAY.filter((p) => p.recordedBy === user?.name)
      : MOCK_PROMISES_TO_PAY;
  }, [useLiveApi, contactLogs, isCallCenterAgent, user?.name]);
  const paymentPlans = useMemo(() => {
    if (useLiveApi) {
      const rows = livePlans.map((plan) => ({
        ...plan,
        customer: planCustomerName(plan),
        instalments: plan.instalments ?? plan.instalment_count ?? plan.instalmentCount,
      }));
      return isCallCenterAgent
        ? rows.filter((p) => (p.createdBy || p.created_by) === user?.name)
        : rows;
    }
    return isCallCenterAgent
      ? MOCK_PAYMENT_PLANS.filter((p) => p.createdBy === user?.name)
      : MOCK_PAYMENT_PLANS;
  }, [useLiveApi, livePlans, isCallCenterAgent, user?.name]);

  const stats = [
    { label: "Calls logged", value: loading ? "…" : callSummary?.total_logs ?? contactLogs.length, icon: Phone, tone: "primary" },
    {
      label: "Promises recorded",
      value: loading ? "…" : callSummary?.promises_recorded ?? promisesToPay.length,
      sub: isCallCenterAgent ? "Logged by you" : "From contact outcomes",
      icon: Handshake,
      tone: "emerald",
    },
    { label: "Follow-ups", value: loading ? "…" : callSummary?.follow_ups ?? contactLogs.filter((l) => l.followUp).length, icon: Calendar, tone: "accent" },
    { label: "Logged today", value: loading ? "…" : callSummary?.today ?? 0, icon: Target, tone: "amber" },
  ];

  const callColumns = useMemo(
    () => [
      { key: "id", header: "Log ID", type: "id" },
      { key: "loggedAt", header: "Date & time", type: "datetime" },
      ...personNameColumns(),
      {
        key: "caseId",
        header: "Case",
        render: (row) => (
          <span className="whitespace-nowrap font-mono text-[12px] font-semibold tracking-wide text-primary/75">
            {row.caseId}
          </span>
        ),
      },
      { key: "agent", header: "Logged by", type: "name" },
      { key: "type", header: "Channel", type: "channel" },
      {
        key: "phoneNumber",
        header: "Phone used",
        render: (row) => row.phoneNumber || null,
      },
      {
        key: "contactResult",
        header: "Contact result",
        render: (row) => row.contactResult || null,
      },
      {
        key: "outcome",
        header: "Outcome",
        render: (row) => <OutcomeSummaryBadge outcome={row.outcome} />,
      },
      {
        key: "summary",
        header: "Outcome summary",
        render: (row) =>
          row.summary ? (
            <span className="block max-w-[220px] truncate text-xs text-primary/70" title={row.summary}>
              {row.summary}
            </span>
          ) : null,
      },
      {
        key: "actions",
        header: "",
        actions: (row) => [
          {
            key: "outcome-details",
            label: "View outcome",
            description: "Full contact outcome record",
            icon: FileText,
            tone: "primary",
            onClick: () => setSelectedLog(row),
          },
        ],
      },
    ],
    [],
  );

  const ptpColumns = [
    { key: "id", header: "PTP ID", type: "id" },
    ...personNameColumns(),
    { key: "caseId", header: "Case", type: "id" },
    { key: "amount", header: "Promised", type: "currency" },
    { key: "dueDate", header: "Due date", type: "date" },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => <StatusBadge status={row.status === "kept" ? "completed" : row.status} />,
    },
    { key: "recordedBy", header: "Recorded by", type: "name" },
  ];

  const planColumns = [
    { key: "id", header: "Plan ID", type: "id" },
    ...personNameColumns(),
    { key: "account_number", header: "Account", type: "id" },
    { key: "caseId", header: "Case", type: "id" },
    { key: "start_date", header: "Start", type: "date" },
    { key: "end_date", header: "End", type: "datetime" },
    { key: "totalAmount", header: "Plan total", type: "currency" },
    { key: "amountOwed", header: "Amount owed", type: "currency" },
    { key: "amountPaid", header: "Amount paid", type: "currency" },
    { key: "instalmentAmount", header: "Instalment", type: "currency" },
    {
      key: "progress",
      header: "Progress",
      type: "number",
      render: (row) => {
        const pct = (row.paidInstalments / row.instalments) * 100;
        return (
          <div className="min-w-[100px]">
            <p className="mb-1 text-xs font-semibold tabular-nums text-primary/70">
              {row.paidInstalments}/{row.instalments}
            </p>
            <div className="h-1.5 overflow-hidden rounded-full bg-primary/[0.08]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    { key: "nextDue", header: "Next due", type: "date" },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => <StatusBadge status={row.status === "broken" ? "failed" : row.status} />,
    },
  ];

  const tableConfig = {
    calls: {
      columns: callColumns,
      data: contactLogs,
      config: { ...CONTACT_CALLS_FILTER_CONFIG, title: "Call log filters" },
    },
    ptp: {
      columns: ptpColumns,
      data: promisesToPay,
      config: { ...PTP_FILTER_CONFIG, title: "Promise to pay filters" },
    },
    plans: {
      columns: planColumns,
      data: paymentPlans,
      config: { ...PLANS_FILTER_CONFIG, title: "Payment plan filters" },
    },
  };

  const { columns, data, config } = tableConfig[activeTab];

  const contactHistoryHref = (nextTab) => {
    const params = new URLSearchParams();
    if (customerParam) params.set("customer", customerParam);
    params.set("tab", nextTab);
    const qs = params.toString();
    return qs ? `/dashboard/contact-centre?${qs}` : "/dashboard/contact-centre";
  };

  return (
    <PageShell
      title="Contact centre"
      description={
        isSuperAdmin || isExecutiveManager
          ? "Agent engagement — calls, payment plans, promises to pay, and customer education."
          : isCallCenterManager
            ? "Engagements on cases for the institutions assigned to you."
            : isLegalOfficer
              ? "Engagements on escalations assigned to you."
              : "Your engagements — calls, payment plans, and promises to pay."
      }
    >
      {customerParam && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <Headphones className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Contact history</p>
              <p className="text-sm font-bold text-primary">{customerParam}</p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/contact-centre" fallbackLabel="Show all contacts" />
        </div>
      )}

      <StatCardGrid cols={4}>
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </StatCardGrid>

      <p className="mb-4 text-xs text-primary/45">
        {loading ? "Loading contact history…" : `${promisesToPay.length} promises recorded · SimCollect logs contact outcomes after every call`}
      </p>

      <div className="mb-4 flex flex-nowrap gap-2 overflow-x-auto pb-0.5">
        {TABS.map((t) =>
          customerParam ? (
            <Link
              key={t.id}
              to={contactHistoryHref(t.id)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeTab === t.id
                  ? "bg-primary text-white"
                  : "bg-white text-primary/50 ring-1 ring-primary/10 hover:text-primary"
              }`}
            >
              {t.label}
            </Link>
          ) : (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeTab === t.id
                  ? "bg-primary text-white"
                  : "bg-white text-primary/50 ring-1 ring-primary/10 hover:text-primary"
              }`}
            >
              {t.label}
            </button>
          )
        )}
      </div>

      <FilteredDataTable
        key={`${activeTab}-${customerParam ?? "all"}`}
        data={data}
        columns={columns}
        config={config}
        initialFilters={initialFilters}
        pageSize={activeTab === "calls" ? 25 : 10}
      />

      <ContactOutcomeModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </PageShell>
  );
}
